import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './StudentRegistrationPage.css';
import StudentSchoolConfirm from './StudentSchoolConfirm';
import StudentDetailsForm from './StudentDetailsForm';
import StudentPreview from './StudentPreview';
import StudentStatusPage from './StudentStatusPage';
import { trackStepView } from '../lib/analytics';

const studentStepNames = {
  1: 'school_confirmation',
  2: 'basic_details',
  3: 'additional_details',
  4: 'review_and_submit',
  5: 'submission_status'
};

function StudentRegistrationPage({ schoolId }) {
  const [step, setStep] = useState(1);
  const [schoolDetails, setSchoolDetails] = useState(null);
  const [isLoadingSchoolDetails, setIsLoadingSchoolDetails] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submissionMessage, setSubmissionMessage] = useState('');
  const [studentFormData, setStudentFormData] = useState({
    student_name: '',
    phone: '',
    confirm_phone: '',
    gender: '',
    grade: '',
    language: '',
    agreeTerms: false
  });
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  useEffect(() => {
    document.title = 'Student Registration - TAP';
  }, []);

  useEffect(() => {
    trackStepView({
      flow: 'student_registration',
      step,
      stepName: studentStepNames[step] || `step_${step}`,
      pagePath: `/student/${schoolId}/step-${step}`,
      pageTitle: `Student Registration Step ${step}`,
      status: step === 5 ? submissionStatus || 'completed' : 'in_progress'
    });
  }, [schoolId, step, submissionStatus]);

  useEffect(() => {
    const verifySchool = async () => {
      setIsLoadingSchoolDetails(true);

      try {
        const response = await axios.post(
          `${apiBaseUrl}/api/method/tap_lms.onboarding.student_registration.verify_school_by_id`,
          { school_id: schoolId }
        );

        const verifiedSchool =
          response.data?.message?.school ||
          response.data?.message?.data ||
          response.data?.message ||
          response.data?.school ||
          response.data ||
          null;

        setSchoolDetails(verifiedSchool && typeof verifiedSchool === 'object' ? verifiedSchool : null);
      } catch (error) {
        console.error('[Student Registration] verify_school_by_id failed:', error);
        setSchoolDetails(null);
      } finally {
        setIsLoadingSchoolDetails(false);
      }
    };

    verifySchool();
  }, [apiBaseUrl, schoolId]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;

    setStudentFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionStatus(null);
    setSubmissionMessage('');

    const payload = {
      school_id: schoolDetails?.school_id || schoolId,
      student_name: studentFormData.student_name.trim(),
      phone: studentFormData.phone,
      gender: studentFormData.gender,
      grade: studentFormData.grade,
      language: studentFormData.language
    };

    try {
      const response = await axios.post(
        `${apiBaseUrl}/api/method/tap_lms.onboarding.student_registration.create_student_web`,
        payload
      );
      setSubmissionStatus('success');
      setSubmissionMessage(
        response.data?.message?.message ||
        response.data?.message ||
        'Student registered successfully.'
      );
    } catch (error) {
      console.error('[Student Registration] create_student_web failed:', error);
      setSubmissionStatus('error');
      setSubmissionMessage(
        error.response?.data?.message?.message ||
        error.response?.data?.message ||
        'Student registration failed.'
      );
    } finally {
      setStep(5);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="student-page-shell">
      <div className="student-card">
        <span className="student-brand">
          THE <span className="student-brand-highlight">APPRENTICE</span> PROJECT
        </span>
        <p className="student-page-title">Student Registration</p>
        <p className="student-page-subtitle">
          Use this link to register for school <strong>{schoolDetails?.school_name || schoolId}</strong>.
        </p>

        <div className="student-progress">
          {[1, 2, 3, 4].map((progressStep) => (
            <div
              key={progressStep}
              className={`student-progress-step ${progressStep <= Math.min(step, 4) ? 'active' : ''}`}
            >
              {progressStep}
            </div>
          ))}
        </div>

        {step === 1 && (
          <StudentSchoolConfirm
            schoolDetails={schoolDetails}
            schoolId={schoolId}
            onConfirm={() => setStep(2)}
            onBackHome={() => {
              window.location.pathname = '/';
            }}
            isLoading={isLoadingSchoolDetails}
          />
        )}

        {step === 2 && (
          <StudentDetailsForm
            formData={studentFormData}
            handleInputChange={handleInputChange}
            onProceed={() => setStep(3)}
            variant="basic"
          />
        )}

        {step === 3 && (
          <StudentDetailsForm
            formData={studentFormData}
            handleInputChange={handleInputChange}
            onProceed={() => setStep(4)}
            onBack={() => setStep(2)}
            variant="additional"
          />
        )}

        {step === 4 && schoolDetails && (
          <StudentPreview
            schoolDetails={schoolDetails}
            formData={studentFormData}
            onEdit={() => setStep(3)}
            handleInputChange={handleInputChange}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        )}

        {step === 5 && (
          <StudentStatusPage
            status={submissionStatus}
            message={submissionMessage}
            schoolName={schoolDetails?.school_name || schoolId}
          />
        )}
      </div>
    </div>
  );
}

export default StudentRegistrationPage;
